import { Component, Prop, h, Host, Element, Build, getAssetPath } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const ITEM_ICONS = [
  'https://marketing-static.signnow.com/347/images/me/cta-type-12/logo-roi.svg',
  'https://marketing-static.signnow.com/347/images/me/cta-type-12/logo-pricing.svg',
  'https://marketing-static.signnow.com/347/images/me/cta-type-12/logo-security.svg',
];

/**
 * "Why choose airSlate SignNow" call-to-action section.
 * Light-blue two-column layout with label, heading, a 3-item feature list, and an animated signature illustration.
 * The feature list is driven by the `items` prop; text content (label, heading, button) is slot-based.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Call-to-action component based on "Why choose airSlate signNow" section
 * @example
 *   <sn-call-to-action-type-4
 *     items='[{"title":"Free 7-day trial.","description":"Choose the plan you need and try it risk-free."},{"title":"Honest pricing for full-featured plans.","description":"airSlate SignNow offers subscription plans with no overages or hidden fees at renewal."},{"title":"Enterprise-grade security.","description":"airSlate SignNow helps you comply with global security standards."}]'
 *   >
 *     <me-text slot="label" tag="span" class="me-text--label">be ready to get more</me-text>
 *     <me-heading slot="heading" variant="2" tag="h2">Why choose airSlate SignNow</me-heading>
 *     <me-button slot="action-primary" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Start free trial</me-button>
 *   </sn-call-to-action-type-4>
 * @slot label - <me-text tag="span" class="me-text--label"> - Small label above the heading
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main section heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Primary action button
 */
@Component({
  tag: 'sn-call-to-action-type-4',
  styleUrl: 'index.scss',
  shadow: false,
  assetsDirs: ['.'],
})
export class SNCallToActionType4 {
  @Element() el: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Background color of the CTA card. Accepts any CSS color or token reference.
   * When omitted, the default light royal-blue is used. Intended for dark backgrounds
   * (e.g. "#111f4b"): setting it switches the card text/label to light for contrast,
   * while the action button keeps its default styling.
   */
  @Prop() background?: string;

  /**
   * Feature list items (up to 3 displayed)
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Feature title (displayed bold)
   * @item.property {string} description - Feature description text
   * @item.content title description
   * @maxItems 3
   */
  @Prop({ mutable: true }) items?: any[] | string;

  private get parsedItems(): any[] {
    return Array.isArray(this.items) ? this.items : [];
  }

  private animation: any;
  private loadObserver: IntersectionObserver;
  private playPauseObserver: IntersectionObserver;
  private lottieLoaded: boolean = false;

  componentWillLoad() {
    this.items = parseJsonProp(this.items, []);
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;

    const initObserver = () => {
      if (!Build.isBrowser || !this.el) return;
      const container = this.el.querySelector('#animatedSignatureExample') as HTMLElement;
      if (!container) return;

      this.loadObserver = new IntersectionObserver(
        async entries => {
          for (const entry of entries) {
            if (entry.isIntersecting && !this.lottieLoaded) {
              this.lottieLoaded = true;
              await this.loadLottieAnimation(container);
              this.loadObserver.disconnect();
              break;
            }
          }
        },
        { rootMargin: '200px', threshold: 0 },
      );
      this.loadObserver.observe(this.el);
    };

    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(initObserver, { timeout: 100 });
    } else {
      setTimeout(initObserver, 0);
    }
  }

  private async loadLottieAnimation(container: HTMLElement) {
    if (!Build.isBrowser) return;
    try {
      const lottieModule = await import('lottie-web/build/player/lottie_light.min.js');
      const lottie = lottieModule.default || lottieModule;
      if (!lottie) return;

      const animationPath = getAssetPath('./animationData.json');
      const animationDataResponse = await fetch(animationPath);
      if (!animationDataResponse.ok) throw new Error(`Failed to load animation data: ${animationDataResponse.status}`);
      const animationData = await animationDataResponse.json();

      container.innerHTML = '';
      this.animation = lottie.loadAnimation({
        container,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        animationData,
      });

      if (typeof IntersectionObserver !== 'undefined') {
        this.playPauseObserver = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) this.animation.play();
              else this.animation.pause();
            });
          },
          { threshold: 0.5 },
        );
        this.playPauseObserver.observe(container);
      }
    } catch (error) {
      console.error('Error loading Lottie animation:', error);
    }
  }

  disconnectedCallback() {
    if (this.animation) this.animation.destroy();
    if (this.loadObserver) this.loadObserver.disconnect();
    if (this.playPauseObserver) this.playPauseObserver.disconnect();
  }

  render() {
    const items = this.parsedItems;

    return (
      <Host data-color-mode={this.colorMode} style={this.background ? { '--sn-cta-type-4-bg': this.background } : undefined}>
        <me-section class={`me-section-call-to-action-type-4${this.background ? ' me-section-call-to-action-type-4--dark' : ''}`} yGap="2xl" yGapLg="5xl" role="region" aria-label="Call to Action">
          <me-row yGapLg="none" yGap="none" xGapLg="none" xGap="none">
            <me-column class="page-section__column page-section__column--primary">
              <div class="title-illustration title-illustration__blue">
                <slot name="label"></slot>
              </div>
              <div class="section-subtitle">
                <slot name="heading"></slot>
              </div>
              <ul class="cta-list">
                {items.slice(0, 3).map((item, i) => (
                  <li class={`cta-list__item${i === 1 ? ' js-ready-animate-example' : ''}`}>
                    <div class="cta-list__logo">
                      <me-icon name={ITEM_ICONS[i]} />
                    </div>
                    <me-text class="cta-list__description">
                      <strong>{item.title}</strong>{item.description ? ` ${item.description}` : ''}
                    </me-text>
                  </li>
                ))}
              </ul>
              <div class="free-trial free-trial__cta">
                <slot name="action-primary"></slot>
              </div>
            </me-column>
            <me-column class="page-section__column page-section__column--illustration">
              {Build.isBrowser && <div id="animatedSignatureExample"></div>}
              <me-picture
                class="lazyload"
                src="https://marketing-static.signnow.com/349/images/home-new/signature-example.svg"
                alt="illustrations signature"
                aspectRatio="1/1"
                lazy={true}
              />
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
